import { useGlobalContext } from '~/context/GlobalContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IChatRoom, IChatUser } from '~/types/common';
import { ComputerIcon, MenuActionIcon, PhoneIcon, UserPlusIcon } from '../Icons/Icons';
import { useViewportResize } from '~/hooks/useViewportResize';
import { usePrivateMessageClick } from '~/hooks/usePrivateMessageClick';
import { openAvatarLightbox } from '~/components/AvatarLightbox/openAvatarLightbox';
import classNames from 'classnames';
import styles from './PeopleContainer.module.css';

const getAvatarSrc = (gender?: 'male' | 'female' | string) => {
  if (gender === 'female') return '/img/common/female-white.svg';
  return '/img/common/male-white.svg';
};

function PeopleContainer() {
  const { context, setContext } = useGlobalContext();
  const resize = useViewportResize();
  const peopleContainerRef = useRef<HTMLDivElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<{ id: string; top: number; left: number } | null>(null);
  const isActive = context.activeSidebarId === 'people';

  useEffect(() => {
    const handleClose = (event: Event) => {
      if (event.type === 'scroll') {
        setActiveDropdown(null);
        return;
      }
      const target = event.target as HTMLElement;
      if (
        !target.closest(`.${styles.dropdownWrapper}`) &&
        !dropdownPortalRef.current?.contains(target)
      ) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClose);
        document.addEventListener('touchstart', handleClose);
        window.addEventListener('scroll', handleClose, { capture: true });
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClose);
        document.removeEventListener('touchstart', handleClose);
        window.removeEventListener('scroll', handleClose, { capture: true });
      };
    }
  }, [activeDropdown]);

  const currentUserId = context?.user?.id || JSON.parse(window.sessionStorage.getItem('user') || '{}').id;

  // const filteredUsers = useMemo(() => {
  //   const query = context.searchQuery?.toLowerCase().trim() || '';
  //   let users = [...(context?.room?.users || [])];
  //   users.sort((a, b) => (a.id === currentUserId ? -1 : b.id === currentUserId ? 1 : 0));

  //   if (!query) return users;
  //   return users.filter((u: IChatUser) => u.name.toLowerCase().includes(query));
  // }, [context?.room?.users, context.searchQuery, currentUserId]);

  // Flaga wyłączająca pokazywanie nieaktywnych (szara kropka) użytkowników na liście "osoby".
  // Gdy true -> nieaktywni zarejestrowani użytkownicy NIE pojawiają się w zakładce osoby.
  // Ustaw na false, aby przywrócić poprzednie zachowanie (wyświetlanie z szarą kropką).
  const HIDE_INACTIVE_USERS = true;

  const filteredUsers = useMemo(() => {
    const query = context.searchQuery?.toLowerCase().trim() || '';
    const rawUsers = context?.room?.users || [];

    // 1. Deduplikacja po nicku (name)
    const uniqueUsersMap = new Map<string, IChatUser>();

    rawUsers.forEach((u: IChatUser) => {
      const existing = uniqueUsersMap.get(u.name);
      // Jeśli użytkownika jeszcze nie ma na liście LUB ten aktualnie sprawdzany to "Ja",
      // nadpisujemy wpis. Dzięki temu zawsze zachowamy naszą aktywną sesję do odznaki "Ty".
      if (!existing || u.id === currentUserId) {
        uniqueUsersMap.set(u.name, u);
      }
    });

    // Zamiana z powrotem na tablicę
    let users = Array.from(uniqueUsersMap.values());

    // 1b. Ukrycie nieaktywnych użytkowników (mechanizm szarej kropki).
    // Nieaktywni (status === 'inactive') – niezależnie od roli – nie są pokazywani.
    // Bieżący użytkownik ("Ja") zawsze pozostaje widoczny.
    if (HIDE_INACTIVE_USERS) {
      users = users.filter((u: IChatUser) => u.status !== 'inactive' || u.id === currentUserId);
    }

    // 2. Sortowanie: "Ja" ląduje zawsze na samej górze
    users.sort((a, b) => (a.id === currentUserId ? -1 : b.id === currentUserId ? 1 : 0));

    // 3. Filtrowanie po wyszukiwarce
    if (!query) return users;
    return users.filter((u: IChatUser) => u.name.toLowerCase().includes(query));
  }, [context?.room?.users, context.searchQuery, currentUserId, HIDE_INACTIVE_USERS]);

  // Liczba widocznych osób w pokoju (po deduplikacji i ukryciu nieaktywnych),
  // niezależna od aktualnego wyszukiwania – używana w nagłówku listy.
  const visiblePeopleCount = useMemo(() => {
    const rawUsers = context?.room?.users || [];
    const uniqueUsersMap = new Map<string, IChatUser>();
    rawUsers.forEach((u: IChatUser) => {
      const existing = uniqueUsersMap.get(u.name);
      if (!existing || u.id === currentUserId) {
        uniqueUsersMap.set(u.name, u);
      }
    });
    let users = Array.from(uniqueUsersMap.values());
    if (HIDE_INACTIVE_USERS) {
      users = users.filter((u: IChatUser) => u.status !== 'inactive' || u.id === currentUserId);
    }
    return users.length;
  }, [context?.room?.users, currentUserId, HIDE_INACTIVE_USERS]);

  const handleItemClick = usePrivateMessageClick();

  const toggleDropdown = (e: React.MouseEvent, item: IChatUser) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeDropdown?.id === item.id) {
      setActiveDropdown(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const isModerator = context.authUser?.role === 'moderator' || context.authUser?.role === 'administrator';
    const estimatedMenuHeight = isModerator ? 150 : 55;
    const menuWidth = 170;
    const spaceBelow = window.innerHeight - rect.bottom;
    setActiveDropdown({
      id: item.id,
      top: spaceBelow >= estimatedMenuHeight ? rect.bottom + 8 : rect.top - estimatedMenuHeight - 8,
      left: Math.max(4, rect.right - menuWidth),
    });
  };

  useEffect(() => {
    if (!isActive) return;
    const fetchPeople = () => {
      window.dispatchEvent(new CustomEvent('getPeople', {
        bubbles: true,
        detail: { roomId: context?.room?.id },
      }));
    };
    fetchPeople();
    const interval = setInterval(fetchPeople, 2000);
    return () => clearInterval(interval);
  }, [isActive, context.room?.id]);

  const handleRoomClick = (currentRoom: IChatRoom | null) => {
    if (!currentRoom) return;
    setContext(prev => ({
      ...prev,
      activeSidebarId: '',
      room: { ...currentRoom, unreadCount: 0 },
      rooms: prev.rooms.map(r => r.id === currentRoom.id ? { ...r, unreadCount: 0 } : r)
    }));
  };

  return (
    <div
      ref={peopleContainerRef}
      className={classNames(styles.peopleContainer, { [styles.active]: isActive })}
      style={{
        transform: resize.isKeyboardOpen
          ? `translateY(${(resize.initialHeight ?? resize.height) - resize.height}px)`
          : 'translateY(0)'
      }}>

      <div className={styles.listHeader} onClick={() => handleRoomClick(context.room || null)}>
        {context?.room?.name}: <span>{visiblePeopleCount || 0} osób</span>
      </div>

      <div className={styles.scrollArea}>
        <div id="people-list" className={styles.peopleList}>
          {filteredUsers.map((item: IChatUser) => {
            const isMe = item.id === currentUserId;
            const avatarSrc = getAvatarSrc(item.gender);
            const userAvatar = item.avatar || null;
            const deviceIcon = item.deviceType === 'mobile' ? <PhoneIcon /> : <ComputerIcon />;

            const handleAvatarClick = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              openAvatarLightbox({ userName: item.name, avatarUrl: userAvatar });
            };

            return (
              <div
                key={item.id}
                className={classNames(styles.peopleItem, { [styles.isMe]: isMe })}
              >
                <div
                  className={styles.avatarWrapper}
                  onClick={handleAvatarClick}
                  title="Pokaż zdjęcia"
                >
                  {userAvatar ? (
                    <img className={styles.avatarPhoto} src={userAvatar} alt={item.name} />
                  ) : (
                    <img className={styles.image} src={avatarSrc} alt="avatar" />
                  )}
                  <span className={classNames(styles.statusDot, { [styles.statusDotInactive]: item.status === 'inactive' })}></span>
                </div>

                <div className={styles.name}>{item.name}</div>
                <div className={styles.deviceIcon}>{deviceIcon}</div>

                <div className={styles.actionSection}>
                  {isMe ? (
                    <span className={styles.meBadge}>Ty</span>
                  ) : (
                    <div className={styles.dropdownWrapper}>
                      <button
                        className={styles.dropdownToggle}
                        onClick={(e) => toggleDropdown(e, item)}
                      >
                        <MenuActionIcon className={styles.menuActionIcon} />
                      </button>

                      {activeDropdown?.id === item.id && ReactDOM.createPortal(
                        <div
                          ref={dropdownPortalRef}
                          className={styles.dropdownMenuPortal}
                          style={{ top: activeDropdown.top, left: activeDropdown.left }}
                        >
                          <div className={styles.dropdownItem}
                            onClick={() => handleItemClick(item, currentUserId, () => setActiveDropdown(null))}
                          >
                            <span>📩</span> Wiadomość
                          </div>

                          {(context.authUser?.role === 'moderator' || context.authUser?.role === 'administrator') && item.role !== 'administrator' && (
                            <>
                              <div
                                className={styles.dropdownItem}
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('banWarning', {
                                    detail: {
                                      targetUserId: item.id,
                                      targetUserName: item.name,
                                    }
                                  }));
                                  setActiveDropdown(null);
                                }}
                              >
                                <span>⚠️</span> Ostrzeż o banie
                              </div>

                              <div
                                className={styles.dropdownItem}
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('kickUser', {
                                    detail: {
                                      targetUserId: item.id,
                                      targetUserName: item.name,
                                    }
                                  }));
                                  setActiveDropdown(null);
                                }}
                              >
                                <span>🚪</span> Wyloguj
                              </div>

                              <div
                                className={classNames(styles.dropdownItem, styles.dropdownItemDanger)}
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('banUser', {
                                    detail: {
                                      targetUserId: item.id,
                                      targetUserName: item.name,
                                    }
                                  }));
                                  setActiveDropdown(null);
                                }}
                              >
                                <span>🚫</span> Zablokuj możliwość logowania
                              </div>
                            </>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PeopleContainer;